import { createContext, useContext, useState, useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { usersAPI } from '@/services/api/users.api';
import { useAuth } from '@/hooks/useAuth';

export interface XPContextType {
  /** Saldo gastable (XP ganado − gastado en recompensas); se muestra en el badge del header. */
  xp: number | undefined;
  refreshXp: () => Promise<void>;
}

export const XPContext = createContext<XPContextType | undefined>(undefined);

interface XPProviderProps {
  children: ReactNode;
}

export function XPProvider({ children }: XPProviderProps) {
  const { user } = useAuth();
  const [xp, setXp] = useState<number | undefined>(undefined);

  const refreshXp = useCallback(async () => {
    if (!user?.id) return;
    const stats = await usersAPI.getStats();
    setXp(stats.walletBalance);
  }, [user?.id]);

  useEffect(() => {
    void refreshXp();
  }, [refreshXp]);

  const value = useMemo<XPContextType>(() => ({ xp, refreshXp }), [xp, refreshXp]);

  return <XPContext.Provider value={value}>{children}</XPContext.Provider>;
}

export function useXP(): XPContextType {
  const context = useContext(XPContext);
  if (context === undefined) {
    throw new Error('useXP must be used within an XPProvider');
  }
  return context;
}
